// @flow
import React, {useState} from 'react';
import type {Node} from 'react';
import {useNavigate} from 'react-router-dom';
import type {Group} from 'api/v2';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  IconNames,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@wellstone-solutions/web';
import {useTheme} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {Theme} from '@wellstone-solutions/common';
import {AddMemberToGroup} from '../AddMemberToGroup';
import {ActionButton as HeaderActionButton} from 'components/PageHeader';

type PropsType = {
  group: Group,
  groupMemberListRefetch: () => void,
};

export const ActionButton = ({
  group,
  groupMemberListRefetch,
}: PropsType): Node => {
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navigate = useNavigate();

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const closeModal = () => {
    setShowAddMemberModal(false);
  };

  return (
    <>
      <HeaderActionButton
        label="Actions"
        iconName={isMobile ? IconNames.MoreVert : IconNames.NavArrowDown}
        onClick={handleClick}
      />

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{horizontal: 'right', vertical: 'top'}}
        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}>
        <MenuItem
          onClick={() => {
            setShowAddMemberModal(true);
            setAnchorEl(null);
          }}>
          <Icon name={IconNames.Plus} style={styles.iconItem} />
          <Typography sx={styles.option}>Add member</Typography>
        </MenuItem>

        <MenuItem
          onClick={() => {
            navigate(`/groups/${group.id}/edit`, {
              state: {
                backButtonProps: {
                  label: `Back to ${group.name}`,
                  path: `/groups/${group.id}`,
                },
              },
            });
          }}>
          <Icon name={IconNames.EditPencil} style={styles.iconItem} />
          <Typography sx={styles.option}>Edit group details</Typography>
        </MenuItem>

        <MenuItem
          onClick={() => {
            navigate(`/announcements/${group.id}`);
          }}>
          <Icon name={IconNames.SendSolid} style={styles.iconItem} />
          <Typography sx={styles.option}>Create announcement</Typography>
        </MenuItem>
      </Menu>

      <Dialog fullWidth sx={styles.dialog} open={showAddMemberModal}>
        <DialogTitle sx={styles.title}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between">
            <Typography variant="h5" sx={styles.bold}>
              Add Member to {group?.name}
            </Typography>
            <IconButton onClick={closeModal} icon={IconNames.Xmark} />
          </Stack>
        </DialogTitle>

        <DialogContent>
          <AddMemberToGroup
            group={group}
            closeModal={closeModal}
            groupMemberListRefetch={groupMemberListRefetch}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

const styles = {
  bold: {
    fontWeight: 700,
  },
  iconItem: {strokeWidth: '2px', color: Theme.colorPalette.dark},
  dialog: {
    '& .MuiPaper-root': {
      maxWidth: {xs: '98%', sm: '85%', md: '70%', lg: '60%'},
      borderRadius: 3,
    },
  },
  title: {
    mt: 2,
  },
  option: {
    paddingLeft: 1,
  },
};
